import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, X, Info } from 'lucide-react';
import { connect } from 'react-redux';
import { getSkills } from '../../../store/actions';
import { getAllUsersBySkills, openMainModal } from '../../../store/actions/profileActions';
import { RubroOption } from '../../../types/newProject';
import styles from './RubroSelector.module.scss';

const CONTACT_EMAIL = 'contacto@edify.la';

interface RubroSelectorProps {
  onRubroSelect: (rubroId: number, rubroName: string) => void;
  existingRubros: Array<{ name: string; id?: string }>;
  selectedRubro?: string;
  onClear?: () => void;
  disabled?: boolean;
  skillsList: any[];
  skillsCatalog: Array<{ id: number; name: string }>;
  getAllUsersBySkills: () => void;
  getSkills: () => void;
  openMainModal: (modalData: any) => void;
}

const RubroSelector: React.FC<RubroSelectorProps> = ({
  onRubroSelect,
  existingRubros,
  selectedRubro = "",
  onClear,
  disabled = false,
  skillsList,
  skillsCatalog,
  getAllUsersBySkills,
  getSkills,
  openMainModal,
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [showCompleteList, setShowCompleteList] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [isLoading] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getAllUsersBySkills();
    getSkills();
  }, [getAllUsersBySkills, getSkills]);

  const validateInput = useCallback((value: string) => {
    const regex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/;

    if (value.length > 0 && value.length < 3) {
      setIsValid(false);
      setValidationMessage('Mínimo 3 caracteres');
    } else if (value.length >= 3 && !regex.test(value)) {
      setIsValid(false);
      setValidationMessage('Solo letras permitidas');
    } else {
      setIsValid(true);
      setValidationMessage('');
    }
  }, []);

  const handleInputChange = useCallback((value: string) => {
    validateInput(value);

    if (value.length < 3) {
      setShowSuggestions(false);
    } else if (value.length >= 3) {
      setShowSuggestions(true);
    }

    setInputValue(value);
  }, [validateInput]);

  const userCountBySkillId = useMemo(() => {
    const m = new Map<number, number>();
    for (const rubro of skillsList) {
      const id = Number(rubro.id);
      if (!Number.isFinite(id)) continue;
      m.set(id, Number(rubro.users) || 0);
    }
    return m;
  }, [skillsList]);

  const suggestionSource = skillsCatalog.length > 0 ? skillsCatalog : skillsList;

  const filteredSuggestions = useMemo(() => {
    const q = inputValue.toLowerCase();
    const rows: RubroOption[] = suggestionSource
      .map((rubro: { id: number | string; name: string }) => {
        const id = Number(rubro.id);
        return {
          id,
          name: rubro.name,
          users: userCountBySkillId.get(id) ?? 0,
        };
      })
      .filter((r) => Number.isFinite(r.id) && r.name.toLowerCase().includes(q));
    rows.sort((a, b) => {
      if (b.users !== a.users) return b.users - a.users;
      return a.name.localeCompare(b.name, 'es');
    });
    return rows;
  }, [suggestionSource, inputValue, userCountBySkillId]);

  const fullCatalogList = useMemo(() => {
    const rows: RubroOption[] = suggestionSource
      .map((rubro: { id: number | string; name: string }) => {
        const id = Number(rubro.id);
        return {
          id,
          name: rubro.name,
          users: userCountBySkillId.get(id) ?? 0,
        };
      })
      .filter((r) => Number.isFinite(r.id));
    rows.sort((a, b) => a.name.localeCompare(b.name, 'es'));
    return rows;
  }, [suggestionSource, userCountBySkillId]);

  const handleRubroSelect = useCallback((rubro: RubroOption) => {
    if (existingRubros.some((existingRubro) => existingRubro.name === rubro.name)) {
      openMainModal({
        title: `Ups, ya tienes un rubro con el nombre ${rubro.name}`,
        iconType: "error",
        body: `Si deseas editarlo o eliminarlo, puedes hacerlo desde la lista de "Agregados".`,
        isSuccess: true,
        confirmHandler: null,
      });
      return;
    }

    onRubroSelect(rubro.id, rubro.name);
    setShowSuggestions(false);
    setInputValue("");
    setShowCompleteList(false);
  }, [existingRubros, onRubroSelect, openMainModal]);

  const handleShowAllRubros = useCallback(() => {
    setShowCompleteList(true);
    setShowSuggestions(false);
  }, []);

  const handleCancel = useCallback(() => {
    setInputValue("");
    setShowSuggestions(false);
    setShowCompleteList(false);
    onClear?.();
  }, [onClear]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (containerRef.current && !containerRef.current.contains(target)) {
        setShowSuggestions(false);
        setShowCompleteList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const contactMailSubject = encodeURIComponent('Solicitud de nuevo rubro en Edify');
  const contactMailBody = encodeURIComponent(
    `Hola, necesito agregar el rubro "${inputValue}" a la plataforma.\n\nGracias.`
  );
  const contactMailHref = `mailto:${CONTACT_EMAIL}?subject=${contactMailSubject}&body=${contactMailBody}`;

  return (
    <div ref={containerRef} className={styles.rubroSelector}>
      {selectedRubro !== "" ? (
        <div className={styles.selectedRubro}>
          <div className={styles.selectedRubroLabel}>
            {selectedRubro}
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={disabled}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.inputContainer}>
            <div className={styles.inputWrapper}>
              <Search size={18} className={styles.searchIcon} />
              <input
                type="text"
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                onFocus={() => {
                  if (inputValue.length >= 3) {
                    setShowSuggestions(true);
                  }
                }}
                placeholder="Ingresá al menos 3 caracteres para buscar un rubro del catálogo"
                className={`${styles.input} ${!isValid ? styles.inputError : ''}`}
                disabled={disabled || isLoading}
              />
            </div>

            <button
              type="button"
              className={styles.showAllButton}
              onClick={() => setShowCompleteList(!showCompleteList)}
              disabled={disabled || isLoading}
            >
              {showCompleteList ? 'Ocultar' : 'Ver todos'}
            </button>
          </div>

          {!isValid && validationMessage && (
            <div className={styles.validationMessage}>
              <Info size={14} />
              {validationMessage}
            </div>
          )}

          {showCompleteList && (
            <div className={styles.suggestions}>
              {isLoading ? (
                <div className={styles.loadingItem}>Cargando rubros...</div>
              ) : (
                fullCatalogList.map((rubro) => (
                  <div
                    key={rubro.id}
                    className={styles.suggestionItem}
                    onClick={() => handleRubroSelect(rubro)}
                  >
                    {rubro.name}
                    {rubro.users > 0 && (
                      <span className={styles.userCount}>
                        - hay {rubro.users} {rubro.users === 1 ? "profesional" : "profesionales"} en edify
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {showSuggestions && filteredSuggestions.length > 0 && (
            <div className={styles.suggestions}>
              {filteredSuggestions.map((rubro) => (
                <div
                  key={rubro.id}
                  className={styles.suggestionItem}
                  onClick={() => handleRubroSelect(rubro)}
                >
                  {rubro.name}
                  {rubro.users > 0 && (
                    <span className={styles.userCount}>
                      - hay {rubro.users} {rubro.users === 1 ? "profesional" : "profesionales"} en edify
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {showSuggestions && !isValid && (
            <div className={styles.errorItem}>Ingrese solo letras</div>
          )}

          {showSuggestions && filteredSuggestions.length === 0 && isValid && (
            <div className={styles.noMatchRubro}>
              <div className={styles.noMatchRubroInfo}>
                <Info size={16} />
                <span>
                  No hay rubros que coincidan con tu búsqueda. Elegí uno de la lista con &quot;Ver todos&quot; o probá con otro término.
                  Si necesitás un rubro que no está en Edify, escribinos a{' '}
                  <a href={contactMailHref} className={styles.contactLink}>
                    {CONTACT_EMAIL}
                  </a>
                  .
                </span>
              </div>
              <button
                type="button"
                className={styles.showCatalogButton}
                onClick={handleShowAllRubros}
                disabled={disabled || isLoading}
              >
                Ver todos los rubros
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    skillsList: state.register.skillsList || [],
    skillsCatalog: state.register.skills || [],
  };
};

export default connect(mapStateToProps, {
  getAllUsersBySkills,
  getSkills,
  openMainModal,
})(RubroSelector);
